import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { HelmetProvider } from "react-helmet-async";
import { installOfflinePatch } from "@/lib/offline-patch";
import { installRadixPointerEventsFix } from "@/lib/radix-pointer-events-fix";
import { installBrasiliaTimezone } from "@/lib/brasilia-time";
installBrasiliaTimezone();
import { registerSW } from "virtual:pwa-register";

// Install offline mutation queue BEFORE app boots
installOfflinePatch();

// Fix bug Radix UI: pointer-events:none órfão no body trava cliques após
// fechar Dialog/Select/Dropdown. Sintoma: "só consigo clicar uma vez".
installRadixPointerEventsFix();

// Register service worker only outside iframe / preview hosts
const isInIframe = (() => {
  try { return window.self !== window.top; } catch { return true; }
})();
const isPreviewHost =
  window.location.hostname.includes("id-preview--") ||
  window.location.hostname.includes("lovableproject.com") ||
  window.location.hostname.includes("lovable.app") && window.location.hostname.startsWith("id-preview");
const host = window.location.hostname.toLowerCase();
const path = window.location.pathname.toLowerCase();
// O sistema interno roda em duas URLs:
//  - jftapecariaautomotiva.lovable.app (host dedicado)
//  - www.jftapecaria.store/sistema (rota dentro do domínio principal)
const isSystemHost =
  host === "jftapecariaautomotiva.lovable.app" ||
  path.startsWith("/sistema");
// Site público é apenas o domínio jftapecaria.store fora da rota /sistema
const isPublicSiteHost =
  (host === "www.jftapecaria.store" || host === "jftapecaria.store") &&
  !isSystemHost;

// Service Worker (PWA/offline) só no domínio do SISTEMA.
// No site público e em previews, desregistramos qualquer SW e limpamos caches
// para evitar versões antigas em cache.
const cleanupCaches = async () => {
  try {
    const regs = await navigator.serviceWorker?.getRegistrations();
    await Promise.all((regs ?? []).map((r) => r.unregister()));
  } catch {}
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {}
};

if (isPublicSiteHost || (isPreviewHost && !isSystemHost) || (isInIframe && !isSystemHost)) {
  // Site público / preview: nunca usar SW.
  // Para usuários que tinham versões antigas em cache (SW antigo servindo
  // HTML/JS desatualizado), fazemos UMA limpeza forçada + reload automático.
  const PUBLIC_RESET_FLAG = 'sw-reset-v4-public-cleanup';
  if (!localStorage.getItem(PUBLIC_RESET_FLAG)) {
    cleanupCaches().then(() => {
      try { localStorage.setItem(PUBLIC_RESET_FLAG, '1'); } catch {}
      window.location.reload();
    });
  } else {
    // Não re-limpa em todo carregamento — isso deixava o site lento ao
    // baixar tudo de novo (fontes, imagens, JS). A limpeza única acima
    // já resolve usuários que estavam com versões antigas em cache.
  }
} else {
  // Sistema (jftapecariaautomotiva.lovable.app):
  // Faz UMA limpeza forçada (após mudança de domínio) antes de registrar o SW
  // novo, para evitar que o Service Worker antigo continue servindo o site.
  const RESET_FLAG = 'sw-reset-v3-system-host';
  if (!localStorage.getItem(RESET_FLAG)) {
    cleanupCaches().then(() => {
      localStorage.setItem(RESET_FLAG, '1');
      // Recarrega para garantir que o HTML venha do servidor, não do cache antigo
      window.location.reload();
    });
  } else {
    try { registerSW({ immediate: true }); } catch {}
  }
}

createRoot(document.getElementById("root")!).render(
  <HelmetProvider>
    <App />
  </HelmetProvider>
);
